// api/speaker.js

const API_BASE_URL = 'https://emiratesscholar.com/system/api';

// ==================== جلب بيانات السبيكر ====================
export const fetchSpeakerData = async (eventId = null) => {
  const token = localStorage.getItem('auth_token');

  if (!token) {
    console.warn('⚠️ [fetchSpeakerData] No auth token found');
    return null;
  }

  try {
    const url = eventId
      ? `${API_BASE_URL}/check-speaker?event_id=${eventId}`
      : `${API_BASE_URL}/check-speaker`;

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      }
    });

    if (response.ok) {
      const data = await response.json();
      console.log('✅ [fetchSpeakerData] API Response received');

      const speakerData = mapSpeakerApiToViewData(data);
      const cacheKey = eventId ? `speaker_data_${eventId}` : 'speaker_data';
      localStorage.setItem(cacheKey, JSON.stringify(speakerData));

      return speakerData;
    } else {
      console.error('❌ [fetchSpeakerData] API Error:', response.status);
    }
  } catch (error) {
    console.error('❌ [fetchSpeakerData] Fetch failed:', error);
  }

  // Fallback من localStorage
  const cacheKey = eventId ? `speaker_data_${eventId}` : 'speaker_data';
  const storedData = localStorage.getItem(cacheKey);
  if (storedData) {
    try {
      const data = JSON.parse(storedData);
      console.log('📦 [fetchSpeakerData] Using cached speaker data');
      return data;
    } catch (e) {
      console.error('❌ [fetchSpeakerData] Failed to parse stored data:', e);
    }
  }

  return null;
};

// ==================== جلب قائمة المؤتمرات النشطة للسبيكر ====================
export const fetchActiveSpeakerManagementList = async () => {
  const token = localStorage.getItem('auth_token');

  if (!token) {
    console.warn('⚠️ [fetchActiveSpeakerManagementList] No auth token found');
    return [];
  }

  try {
    const response = await fetch(`${API_BASE_URL}/active-speaker-managment-list`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      }
    });

    if (response.ok) {
      const data = await response.json();
      console.log('✅ [fetchActiveSpeakerManagementList] API Response received');
      return data;
    } else {
      console.error('❌ [fetchActiveSpeakerManagementList] API Error:', response.status);
    }
  } catch (error) {
    console.error('❌ [fetchActiveSpeakerManagementList] Fetch failed:', error);
  }

  return [];
};


// ==================== قبول/رفض دعوة السبيكر ====================
export const updateSpeakerStatus = async (status, eventId = null) => {
  const token = localStorage.getItem('auth_token');

  if (!token) {
    throw new Error('No auth token found');
  }

  const formData = new FormData();
  formData.append('status', status);
  if (eventId) {
    formData.append('event_id', eventId);
  }

  console.log('📤 [updateSpeakerStatus] Sending:', { status, eventId });

  try {
    const response = await fetch(`${API_BASE_URL}/update-speaker-status`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    const responseText = await response.text();
    console.log('📥 [updateSpeakerStatus] Response:', response.status, responseText.substring(0, 200));

    if (response.ok) {
      console.log('✅ [updateSpeakerStatus] Success');

      const refreshedData = await fetchSpeakerData(eventId);

      try {
        return { ...JSON.parse(responseText), refreshedData };
      } catch {
        return { success: true, refreshedData };
      }
    } else {
      console.error('❌ [updateSpeakerStatus] Failed:', response.status);
      throw new Error('Failed to update status');
    }
  } catch (error) {
    console.error('❌ [updateSpeakerStatus] Error:', error);
    throw error;
  }
};

// ==================== رفع مستند للسبيكر ====================
export const uploadSpeakerDocument = async (file, name, chairId, speakerId) => {
  const token = localStorage.getItem('auth_token');

  if (!token) {
    throw new Error('No auth token found');
  }

  const maxSize = 10 * 1024 * 1024;
  if (file.size > maxSize) {
    throw new Error(`File size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds maximum allowed size (10MB)`);
  }

  const formData = new FormData();
  formData.append('name', name);
  formData.append('chair_id', chairId);
  formData.append('speaker_id', speakerId);
  formData.append('file', file);

  console.log('📤 [uploadSpeakerDocument] Uploading:', {
    name,
    chair_id: chairId,
    speaker_id: speakerId,
    fileName: file.name,
    fileSize: (file.size / 1024).toFixed(2) + ' KB',
    fileType: file.type
  });

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000);

    const response = await fetch(`${API_BASE_URL}/user-upload-document`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      },
      body: formData,
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    const responseText = await response.text();
    console.log('📥 [uploadSpeakerDocument] Status:', response.status);
    console.log('📥 [uploadSpeakerDocument] Response:', responseText.substring(0, 500));

    if (response.ok) {
      console.log('✅ [uploadSpeakerDocument] Success');

      const refreshedData = await fetchSpeakerData(chairId);

      try {
        return { ...JSON.parse(responseText), refreshedData };
      } catch {
        return { success: true, refreshedData };
      }
    } else {
      let errorMessage = `Upload failed: ${response.status}`;
      try {
        const errorData = JSON.parse(responseText);
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch { }

      console.error('❌ [uploadSpeakerDocument] Failed:', errorMessage);
      throw new Error(errorMessage);
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      console.error('❌ [uploadSpeakerDocument] Timeout');
      throw new Error('Upload timeout: File may be too large or server is slow. Please try again.');
    }
    console.error('❌ [uploadSpeakerDocument] Error:', error);
    throw error;
  }
};

// ==================== إضافة تعليم للسبيكر ====================
export const addSpeakerEducation = async (data) => {
  const token = localStorage.getItem('auth_token');

  if (!token) {
    throw new Error('No auth token found');
  }

  const formData = new FormData();
  formData.append('startedIn', data.startDate);
  formData.append('completedin', data.endDate || '');
  formData.append('school', data.university);
  formData.append('field', data.field || '');
  formData.append('mainCourse', data.degree);
  formData.append('country', data.country || '');

  console.log('📤 [addSpeakerEducation] Adding education:', {
    mainCourse: data.degree,
    school: data.university,
    field: data.field,
    country: data.country,
    startedIn: data.startDate,
    completedin: data.endDate || '(currently studying)'
  });

  try {
    const response = await fetch(`${API_BASE_URL}/add-user-education`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      },
      body: formData
    });

    const responseText = await response.text();
    console.log('📥 [addSpeakerEducation] Status:', response.status);
    console.log('📥 [addSpeakerEducation] Response:', responseText.substring(0, 300));

    if (response.ok) {
      console.log('✅ [addSpeakerEducation] Success');

      const refreshedData = await fetchSpeakerData();

      try {
        return { ...JSON.parse(responseText), refreshedData };
      } catch {
        return { success: true, refreshedData };
      }
    } else {
      console.error('❌ [addSpeakerEducation] Failed:', response.status);
      throw new Error('Failed to add education');
    }
  } catch (error) {
    console.error('❌ [addSpeakerEducation] Error:', error);
    throw error;
  }
};

// ==================== حذف مستند ====================
export const deleteSpeakerDocument = async (documentId) => {
  const token = localStorage.getItem('auth_token');

  if (!token) {
    throw new Error('No auth token found');
  }

  console.log('📤 [deleteSpeakerDocument] Deleting document:', documentId);

  try {
    const response = await fetch(`${API_BASE_URL}/delete-document/${documentId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      }
    });

    if (response.ok) {
      const result = await response.json();
      console.log('✅ [deleteSpeakerDocument] Success:', result);

      const refreshedData = await fetchSpeakerData();

      return { ...result, refreshedData };
    } else {
      console.error('❌ [deleteSpeakerDocument] API Error:', response.status);
      throw new Error('Failed to delete document');
    }
  } catch (error) {
    console.error('❌ [deleteSpeakerDocument] Failed:', error);
    throw error;
  }
};

// ==================== حذف تعليم ====================
export const deleteSpeakerEducation = async (educationId) => {
  const token = localStorage.getItem('auth_token');

  if (!token) {
    throw new Error('No auth token found');
  }

  console.log('📤 [deleteSpeakerEducation] Deleting education:', educationId);

  try {
    const response = await fetch(`${API_BASE_URL}/delete-education/${educationId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      }
    });

    if (response.ok) {
      const result = await response.json();
      console.log('✅ [deleteSpeakerEducation] Success:', result);

      const refreshedData = await fetchSpeakerData();

      return { ...result, refreshedData };
    } else {
      console.error('❌ [deleteSpeakerEducation] API Error:', response.status);
      throw new Error('Failed to delete education');
    }
  } catch (error) {
    console.error('❌ [deleteSpeakerEducation] Failed:', error);
    throw error;
  }
};

// Helper to parse the speaker's invitation accept status from API data
const parseSpeakerAcceptRequest = (apiData) => {
  const status = apiData.speakerStatus || '';
  const statusLower = String(status).toLowerCase().trim();

  if (statusLower === 'approved' || statusLower === 'accepted' || statusLower === 'confirmed') {
    return true;
  }
  if (statusLower === 'rejected') {
    return false;
  }
  return null; // Pending / no response
};

const mapAgendaList = (agendaList) => {
  if (!agendaList || !Array.isArray(agendaList)) return [];
  return agendaList.map((item) => ({
    day: 'Day 1',
    time: 'TBA',
    duration: 'TBA',
    title: item.session_topic || 'Emirates Scholar Session',
    location: 'TBA',
    attendees: 'TBA',
    tag: item.session_type || 'Keynote Speaker'
  }));
};

const mapHotelBookings = (hotelBookings) => {
  if (!hotelBookings || !Array.isArray(hotelBookings) || hotelBookings.length === 0) return null;
  const booking = hotelBookings[0];
  return {
    checkIn: booking.check_in || 'TBA',
    checkOut: booking.check_out || 'TBA',
    details: {
      hotelName: booking.hotel_name || 'TBA',
      bookingRef: `HB-${booking.id}`,
      roomType: booking.room_type || 'TBA',
      roomNumber: booking.room_number || 'TBA'
    }
  };
};

const mapFlightBookings = (flightBookings) => {
  if (!flightBookings || !Array.isArray(flightBookings) || flightBookings.length === 0) return null;
  const booking = flightBookings[0];
  const formatTime = (tStr) => {
    if (!tStr) return 'TBA';
    return tStr.replace('T', ' ').substring(0, 16);
  };
  return {
    outbound: {
      from: { code: '', city: booking.departure_city || 'TBA', time: formatTime(booking.departure_time) },
      to: { code: '', city: booking.arrival_city || 'TBA', time: formatTime(booking.arrival_time) },
      duration: 'TBA',
      icon: 'flight_takeoff',
      details: {
        airline: booking.airline || 'TBA',
        flightNo: booking.flight_number || 'TBA',
        class: booking.ticket_class || 'TBA',
        seat: 'TBA',
        pnr: `FL-${booking.id}`
      }
    },
    return: null
  };
};

const mapTransportationBookings = (transportationBookings) => {
  if (!transportationBookings || !Array.isArray(transportationBookings) || transportationBookings.length === 0) return null;
  const booking = transportationBookings[0];
  return {
    pickup: {
      from: { place: booking.pickup_location || 'TBA', detail: '' },
      to: { place: booking.dropoff_location || 'TBA', detail: '' },
      dateTime: booking.pickup_time ? booking.pickup_time.replace('T', ' ').substring(0, 16) : 'TBA',
      vehicleType: booking.transport_type || 'TBA',
      driverName: booking.picker_name || 'TBA',
      driverContact: 'TBA'
    },
    return: null
  };
};

// ==================== دالة تحويل بيانات API إلى هيكل العرض ====================
const mapSpeakerApiToViewData = (apiData) => {
  if (!apiData || !apiData.speakerDetails) {
    console.error('❌ [mapSpeakerApiToViewData] No speakerDetails found');
    return null;
  }

  const details = apiData.speakerDetails;

  const userDetails = JSON.parse(localStorage.getItem('user_details') || '{}');

  const avatar = userDetails?.imageLink || '';

  const coverImage = userDetails?.cover
    ? `https://citescholar.com/portal/public/users/${userDetails.cover}`
    : '';

  const title = details.title || userDetails?.title || '';
  const formattedTitle = title ? title.charAt(0).toUpperCase() + title.slice(1) : '';

  const education = (details.education || []).map(edu => ({
    id: edu.id,
    icon: getEducationIcon(edu.mainCourse),
    degree: edu.mainCourse || '',
    details: {
      university: edu.school || '',
      field: edu.field || '',
      country: edu.country || '',
      years: formatYears(edu.startedIn, edu.completedin)
    }
  }));

  const documents = (details.uploadDocument || []).map(doc => ({
    id: doc.id,
    name: doc.name || 'Unknown Document',
    icon: getDocumentIcon(doc.name),
    link: doc.document_link || ''
  }));

  const cleanSpecialNotes = apiData.specialNote ? apiData.specialNote.replace(/<[^>]*>/g, '').trim() : '';

  return {
    speakerId: details.speakerID,
    profile: {
      personalInfo: {
        speakerID: details.speakerID,
        title: formattedTitle,
        fullName: details.fullName || '',
        displayName: formattedTitle
          ? `${formattedTitle} ${details.fullName || ''}`
          : details.fullName || '',
        nationality: details.nationality || '',
        dateOfBirth: details.dateOfBirth || '',
        gender: details.gender || '',
        religion: details.religion || '',
        subReligion: details.subReligion || '',
        profession: details.profession || '',
        company: details.company || '',
        languagesSpoken: details.languagesSpoken || '',
        maritalStatus: details.maritalStatus || '',
        avatar: avatar,
        coverImage: coverImage
      },
      address: {
        currentCountry: details.currentCountry || '',
        city: details.city || '',
        address: details.address || '',
        zipcode: details.zipcode || '',
        contactNumber: details.contactNumber || '',
        isUaeResident: details.isUaeResident ? 'Yes' : 'No',
        passportNumber: details.passportNumber || '',
        passportExpiry: details.passportExpiry || '',
        passportIssue: details.passportIssue || '',
        passportIssuePlace: details.passportIssuePlace || '',
        emiratesIdNumber: details.emiratesIdNumber || '',
        emiratesExpiryDate: details.emiratesExpiryDate || ''
      },
      education: education,
      documents: documents,
      bio: details.bio || ''
    },
    status: {
      speakerMenuShow: apiData.speakerMenuShow || false,
      speakerAcceptRequest: parseSpeakerAcceptRequest(apiData),
      needsAcceptance: apiData.speakerAcceptRequest === true || apiData.speakerAcceptRequest === 'true',
      hotelRequired: apiData.hotelRequired === true || apiData.hotelRequired === 1 || apiData.hotelRequired === '1' || apiData.hotelRequired === 'true',
      flightRequired: apiData.flightRequired === true || apiData.flightRequired === 1 || apiData.flightRequired === '1' || apiData.flightRequired === 'true',
      transportationRequired: apiData.transportationRequired === true || apiData.transportationRequired === 1 || apiData.transportationRequired === '1' || apiData.transportationRequired === 'true',
      notesRequired: apiData.notesRequired === true || apiData.notesRequired === 1 || apiData.notesRequired === '1' || apiData.notesRequired === 'true',
      specialNoted: apiData.specialNoted || false,
      submitForm: apiData.submitForm || false,
      missingFields: apiData.missingFields || [],
      applicationStatus: apiData.applicationStatus || apiData.speakerDetails?.applicationStatus || apiData.speakerDetails?.status || ''
    },
    bookings: {
      hotel: mapHotelBookings(apiData.hotelBookings),
      flight: mapFlightBookings(apiData.flightBookings),
      transport: mapTransportationBookings(apiData.transportationBookings)
    },
    agenda: mapAgendaList(apiData.agendaList),
    flight: mapFlightBookings(apiData.flightBookings),
    hotel: mapHotelBookings(apiData.hotelBookings),
    transport: mapTransportationBookings(apiData.transportationBookings),
    specialNotes: cleanSpecialNotes || details.notes || apiData.notes || details.specialNotes || apiData.specialNotes || ''
  };
};

const formatYears = (startDate, endDate) => {
  if (!startDate) return '';
  const start = startDate.split(' ')[0];
  if (!endDate || endDate === 'null' || endDate === null) {
    return `${start} – Present`;
  }
  const end = endDate.split(' ')[0];
  return `${start} – ${end}`;
};

const getEducationIcon = (degree) => {
  if (!degree) return 'school';
  const d = degree.toLowerCase();
  if (d.includes('doctorate') || d.includes('phd')) return 'workspace_premium';
  if (d.includes('master')) return 'school';
  if (d.includes('bachelor')) return 'menu_book';
  return 'school';
};

const getDocumentIcon = (name) => {
  if (!name) return 'description';
  const n = name.toLowerCase();
  if (n.includes('passport')) return 'passport';
  if (n.includes('visa')) return 'travel_explore';
  if (n.includes('eid') || n.includes('emirates')) return 'id_card';
  if (n.includes('signature')) return 'draw';
  return 'description';
};

// ==================== إرسال نموذج السبيكر للموافقة ====================
export const submitSpeakerFormForApproval = async (speakerId) => {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    throw new Error('No auth token found');
  }

  const formData = new FormData();
  formData.append('speakerID', speakerId);

  const response = await fetch(`${API_BASE_URL}/submit-speaker-form-for-approval`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    body: formData
  });

  if (!response.ok) {
    throw new Error('Failed to submit form for approval');
  }

  return await response.json();
};